import { useState, type FormEvent, type ReactElement } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

function AuthShell({ children }: { children: ReactElement }): ReactElement {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/85 via-primary/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-primary-foreground">
          <Link to="/" className="font-display text-3xl">
            MyPivot
          </Link>
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-accent">Conciergerie</p>
            <p className="font-display mt-4 max-w-md text-4xl leading-tight">
              "Rendre l'impossible possible."
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  )
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: { message?: string } } | undefined
    return data?.error?.message ?? err.message
  }
  return 'Une erreur est survenue'
}

export function LoginPage(): ReactElement {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await login(email, password)
      navigate('/client') // guard will bounce admin to /admin
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <>
        <h1 className="font-display text-3xl">Bon retour parmi nous.</h1>
        <p className="mt-2 text-sm text-muted-foreground">Connectez-vous à votre espace privé.</p>
        <form onSubmit={(e) => void onSubmit(e)} className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
              }}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" isLoading={loading} disabled={loading}>
            {loading ? 'Connexion…' : 'Se connecter'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Pas encore client ?{' '}
            <Link to="/signup" className="text-primary hover:underline">
              Créer un compte
            </Link>
          </p>
        </form>
      </>
    </AuthShell>
  )
}

export function SignupPage(): ReactElement {
  const { signup } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await signup({
        name: form.name,
        email: form.email,
        password: form.password,
        ...(form.phone ? { phone: form.phone } : {}),
      })
      navigate('/client')
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <>
        <h1 className="font-display text-3xl">Devenir client MyPivot.</h1>
        <p className="mt-2 text-sm text-muted-foreground">Accès privé à votre conciergerie.</p>
        <form onSubmit={(e) => void onSubmit(e)} className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nom complet</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => {
                setForm({ ...form, email: e.target.value })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Téléphone (facultatif)</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => {
                setForm({ ...form, phone: e.target.value })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Mot de passe (8 caractères min.)</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => {
                setForm({ ...form, password: e.target.value })
              }}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" isLoading={loading} disabled={loading}>
            {loading ? 'Création…' : 'Créer mon compte'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Déjà client ?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Se connecter
            </Link>
          </p>
        </form>
      </>
    </AuthShell>
  )
}
